import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import adminApiService, {
  AdminStats,
  MenuConfig,
  MenuPartecipante,
  Registration,
  RegistrationEditableFields
} from '../services/adminApiService';
import { colors } from '../styles/GlobalStyles';

const PageWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px 20px 60px;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h2`
  color: ${colors.brown};
  margin: 0;
  font-size: 1.4rem;
`;

const Btn = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger'; $size?: 'sm' }>`
  padding: ${props => props.$size === 'sm' ? '4px 10px' : '9px 16px'};
  font-size: ${props => props.$size === 'sm' ? '12px' : '14px'};
  border-radius: 2px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${props => props.$variant === 'secondary' ? colors.secondary : 'transparent'};
  background: ${props =>
    props.$variant === 'danger' ? '#A33A2F' :
    props.$variant === 'secondary' ? colors.white :
    colors.primary};
  color: ${props => props.$variant === 'secondary' ? colors.brown : colors.white};
  transition: opacity 0.15s ease;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { opacity: 0.9; }
`;

const ErrorBanner = styled.div`
  background: #FBEAE6;
  color: ${colors.accent};
  padding: 12px;
  border-radius: 2px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const StatsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
`;

const StatCard = styled.div<{ $highlight?: boolean }>`
  background: ${props => props.$highlight ? '#FBF6EC' : colors.white};
  border: 1px solid ${props => props.$highlight ? colors.gold : colors.secondary};
  border-radius: 2px;
  padding: 12px 16px;
  min-width: 150px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: ${colors.lightBrown};
  margin-bottom: 4px;
`;

const StatValue = styled.div<{ $color?: string }>`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.$color || colors.brown};
`;

const StatLine = styled.div`
  font-size: 13px;
  color: ${colors.brown};
`;

const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  align-items: center;
`;

const FilterControl = styled.input`
  padding: 8px 10px;
  border: 1px solid ${colors.secondary};
  border-radius: 2px;
  font-size: 13px;
  box-sizing: border-box;

  &:focus { outline: none; border-color: ${colors.primary}; }
`;

const FilterSelect = styled.select`
  padding: 8px 10px;
  border: 1px solid ${colors.secondary};
  border-radius: 2px;
  font-size: 13px;
  background: ${colors.white};

  &:focus { outline: none; border-color: ${colors.primary}; }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid ${colors.secondary};
  border-radius: 2px;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px;
  background: ${colors.primary};
  color: ${colors.white};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  position: sticky;
  top: 0;
  letter-spacing: 0.02em;
`;

const Td = styled.td`
  padding: 8px 10px;
  border-bottom: 1px solid ${colors.secondary};
  vertical-align: top;
  font-size: 13px;
  color: ${colors.brown};
`;

const TableRow = styled.tr`
  &:nth-child(even) { background: ${colors.cream}; }
`;

const CellInput = styled.input`
  width: 100%;
  min-width: 70px;
  padding: 4px 6px;
  border: 1px solid ${colors.secondary};
  border-radius: 2px;
  font-size: 13px;
  box-sizing: border-box;
  font-family: inherit;

  &:focus { outline: none; border-color: ${colors.primary}; }
`;

const PaymentBadge = styled.span<{ $tipo: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 2px;
  font-size: 11px;
  font-weight: 700;
  background: ${props => props.$tipo === 'completo' ? '#E6F0EA' : props.$tipo === 'acconto' ? '#FBF1DD' : '#FBEAE6'};
  color: ${props => props.$tipo === 'completo' ? '#2E7D4F' : props.$tipo === 'acconto' ? '#8A6D1D' : colors.accent};
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${colors.lightBrown};
  text-decoration: underline;
  font-size: 11px;
  cursor: pointer;
  padding: 0;
  margin-top: 4px;
`;

function formatEuro(value: number | undefined | null): string {
  return `€${(value ?? 0).toFixed(2)}`;
}

function paymentTipoLabel(tipo: string): string {
  if (tipo === 'completo') return 'Completo';
  if (tipo === 'acconto') return 'Acconto';
  if (tipo === 'da_pagare') return 'Da pagare';
  return tipo;
}

function menuLabel(menuConfig: MenuConfig | null, categoriaKey: string): string {
  return menuConfig?.categorie.find((c) => c.key === categoriaKey)?.label || categoriaKey;
}

function opzioneValoreLabel(menuConfig: MenuConfig | null, categoriaKey: string, opzioneKey: string, valoreKey: string): string {
  const categoria = menuConfig?.categorie.find((c) => c.key === categoriaKey);
  const opzione = categoria?.opzioni.find((o) => o.key === opzioneKey);
  return opzione?.valori.find((v) => v.key === valoreKey)?.label || valoreKey;
}

// Raggruppa per il piatto scelto (es. stinco/cotoletta), non per la categoria generica
// ("Menu Adulto"): è il dato che serve davvero per i conteggi in cucina.
function etichettaTipoMenu(p: MenuPartecipante, menuConfig: MenuConfig | null): string {
  const categoria = menuConfig?.categorie.find((c) => c.key === p.categoria);
  const opzioneConSelezione = categoria?.opzioni.find((o) => p.selezioni?.[o.key]);
  return opzioneConSelezione
    ? opzioneValoreLabel(menuConfig, p.categoria, opzioneConSelezione.key, p.selezioni[opzioneConSelezione.key])
    : menuLabel(menuConfig, p.categoria);
}

function formatMenuRiepilogo(reg: Registration, menuConfig: MenuConfig | null): string {
  const conteggi = new Map<string, number>();
  let bicchieri = 0;

  reg.menuPartecipanti.forEach((p) => {
    const etichetta = etichettaTipoMenu(p, menuConfig);
    conteggi.set(etichetta, (conteggi.get(etichetta) || 0) + 1);
    if (p.bicchiereCommemorativo) bicchieri += 1;
  });

  const parti = Array.from(conteggi.entries()).map(([etichetta, count]) => `${count}x ${etichetta}`);
  if (bicchieri > 0) parti.push(`${bicchieri}x 🍺`);

  return parti.join(' • ');
}

const LoginForm: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await adminApiService.login(username, password);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Errore di accesso');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 360, margin: '80px auto', padding: 24 }}>
      <Title style={{ textAlign: 'center', marginBottom: 20 }}>Accesso Admin</Title>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 14, color: colors.lightBrown }}>Username</label>
          <FilterControl style={{ width: '100%' }} value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 14, color: colors.lightBrown }}>Password</label>
          <FilterControl style={{ width: '100%' }} type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {error && <div style={{ color: colors.accent, marginBottom: 12, fontSize: 14 }}>{error}</div>}
        <Btn type="submit" disabled={isSubmitting || !username || !password} style={{ width: '100%' }}>
          {isSubmitting ? 'Accesso in corso...' : 'Accedi'}
        </Btn>
      </form>
    </div>
  );
};

const Dashboard: React.FC<{ onLogout: () => void }> = ({ onLogout }) => {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [menuConfig, setMenuConfig] = useState<MenuConfig | null>(null);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [drafts, setDrafts] = useState<Record<string, RegistrationEditableFields>>({});
  const [savingBooking, setSavingBooking] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchText, setSearchText] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('tutte');
  const [pagamentoFiltro, setPagamentoFiltro] = useState('tutti');
  const [tavoloFiltro, setTavoloFiltro] = useState('tutti');

  const [editingPaymentBooking, setEditingPaymentBooking] = useState<string | null>(null);
  const [paymentDraft, setPaymentDraft] = useState<{ totaleDovuto: string; importoPagato: string }>({ totaleDovuto: '', importoPagato: '' });
  const [isSavingPayment, setIsSavingPayment] = useState(false);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [regs, config, statsData] = await Promise.all([
        adminApiService.getRegistrations(),
        adminApiService.getMenuConfig(),
        adminApiService.getStats()
      ]);
      setRegistrations(regs);
      setMenuConfig(config);
      setStats(statsData);
      setDrafts({});
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento dei dati');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const getFieldValue = (reg: Registration, field: keyof RegistrationEditableFields): string => {
    const draft = drafts[reg.bookingNumber];
    if (draft && Object.prototype.hasOwnProperty.call(draft, field)) {
      return (draft[field] as string) ?? '';
    }
    return (reg[field] as string) ?? '';
  };

  const setFieldValue = (bookingNumber: string, field: keyof RegistrationEditableFields, value: string) => {
    setDrafts((prev) => ({
      ...prev,
      [bookingNumber]: { ...prev[bookingNumber], [field]: value }
    }));
  };

  const isDirty = (bookingNumber: string): boolean => !!drafts[bookingNumber] && Object.keys(drafts[bookingNumber]).length > 0;

  const handleSave = async (reg: Registration) => {
    const draft = drafts[reg.bookingNumber];
    if (!draft) return;
    setSavingBooking(reg.bookingNumber);
    setError('');
    try {
      const updated = await adminApiService.updateRegistration(reg.bookingNumber, draft);
      setRegistrations((prev) => prev.map((r) => (r.bookingNumber === updated.bookingNumber ? updated : r)));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[reg.bookingNumber];
        return next;
      });
    } catch (err: any) {
      setError(err.message || 'Errore nel salvataggio');
    } finally {
      setSavingBooking(null);
    }
  };

  const handleDelete = async (reg: Registration) => {
    const conferma = window.confirm(
      `Eliminare definitivamente la prenotazione ${reg.bookingNumber} (${reg.nome} ${reg.cognome})?`
    );
    if (!conferma) return;

    setSavingBooking(reg.bookingNumber);
    setError('');
    try {
      await adminApiService.deleteRegistration(reg.bookingNumber);
      setRegistrations((prev) => prev.filter((r) => r.bookingNumber !== reg.bookingNumber));
      const statsData = await adminApiService.getStats();
      setStats(statsData);
    } catch (err: any) {
      setError(err.message || "Errore nell'eliminazione");
    } finally {
      setSavingBooking(null);
    }
  };

  const startEditPayment = (reg: Registration) => {
    setEditingPaymentBooking(reg.bookingNumber);
    setPaymentDraft({ totaleDovuto: reg.totaleDovuto.toString(), importoPagato: reg.importoPagato.toString() });
    setError('');
  };

  const cancelEditPayment = () => {
    setEditingPaymentBooking(null);
  };

  const savePayment = async (reg: Registration) => {
    const totaleDovuto = parseFloat(paymentDraft.totaleDovuto);
    const importoPagato = parseFloat(paymentDraft.importoPagato);

    if (isNaN(totaleDovuto) || totaleDovuto < 0 || isNaN(importoPagato) || importoPagato < 0) {
      setError('Importi non validi');
      return;
    }
    if (importoPagato > totaleDovuto) {
      setError("L'importo pagato non può superare il totale dovuto");
      return;
    }

    setIsSavingPayment(true);
    setError('');
    try {
      const updated = await adminApiService.updateRegistrationPayment(reg.bookingNumber, { totaleDovuto, importoPagato });
      setRegistrations((prev) => prev.map((r) => (r.bookingNumber === updated.bookingNumber ? updated : r)));
      setEditingPaymentBooking(null);
      const statsData = await adminApiService.getStats();
      setStats(statsData);
    } catch (err: any) {
      setError(err.message || 'Errore nella correzione del pagamento');
    } finally {
      setIsSavingPayment(false);
    }
  };

  const filteredRegistrations = useMemo(() => {
    const testo = searchText.trim().toLowerCase();
    return registrations.filter((reg) => {
      if (testo) {
        const haystack = `${reg.bookingNumber} ${reg.nome} ${reg.cognome} ${reg.email} ${reg.nomeGruppo}`.toLowerCase();
        if (!haystack.includes(testo)) return false;
      }
      if (categoriaFiltro !== 'tutte') {
        if (!reg.menuPartecipanti.some((p) => p.categoria === categoriaFiltro)) return false;
      }
      if (pagamentoFiltro !== 'tutti' && reg.payment.tipo !== pagamentoFiltro) return false;
      if (tavoloFiltro === 'assegnato' && !reg.numeroTavolo) return false;
      if (tavoloFiltro === 'non_assegnato' && reg.numeroTavolo) return false;
      return true;
    });
  }, [registrations, searchText, categoriaFiltro, pagamentoFiltro, tavoloFiltro]);

  const filteredTotals = useMemo(() => {
    const perCategoriaMap = new Map<string, { count: number; incasso: number }>();
    let totalePartecipanti = 0;
    let totaleDovuto = 0;
    let totalePagato = 0;
    let totaleRimanente = 0;
    let bicchieriCount = 0;
    let bicchieriIncasso = 0;

    filteredRegistrations.forEach((reg) => {
      totalePartecipanti += reg.numeroPartecipanti;
      totaleDovuto += reg.totaleDovuto;
      totalePagato += reg.importoPagato;
      totaleRimanente += reg.saldoRimanente;

      reg.menuPartecipanti.forEach((p) => {
        const etichetta = etichettaTipoMenu(p, menuConfig);
        const voce = perCategoriaMap.get(etichetta) || { count: 0, incasso: 0 };
        voce.count += 1;
        voce.incasso += p.prezzoUnitario || 0;
        perCategoriaMap.set(etichetta, voce);

        if (p.bicchiereCommemorativo) {
          bicchieriCount += 1;
          bicchieriIncasso += p.prezzoBicchiere || 0;
        }
      });
    });

    return {
      totaleRegistrazioni: filteredRegistrations.length,
      totalePartecipanti,
      totaleDovuto,
      totalePagato,
      totaleRimanente,
      perCategoria: Array.from(perCategoriaMap.entries()).map(([_id, v]) => ({ _id, ...v })),
      bicchieri: { count: bicchieriCount, incasso: bicchieriIncasso }
    };
  }, [filteredRegistrations, menuConfig]);

  return (
    <PageWrapper>
      <TopBar>
        <Title>Gestione Prenotazioni · Aria di Fest</Title>
        <div>
          <Btn $variant="secondary" onClick={loadAll} style={{ marginRight: 8 }}>
            🔄 Aggiorna
          </Btn>
          <Btn $variant="secondary" onClick={onLogout}>
            Esci
          </Btn>
        </div>
      </TopBar>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {isLoading ? (
        <div style={{ color: colors.lightBrown }}>Caricamento dati...</div>
      ) : (
        <>
          {/* Totali */}
          {stats && (
            <StatsGrid>
              <StatCard>
                <StatLabel>Prenotazioni</StatLabel>
                <StatValue>{stats.totali.totaleRegistrazioni}</StatValue>
              </StatCard>
              <StatCard>
                <StatLabel>Partecipanti</StatLabel>
                <StatValue>{stats.totali.totalePartecipanti}</StatValue>
              </StatCard>
              <StatCard>
                <StatLabel>Totale Dovuto</StatLabel>
                <StatValue>{formatEuro(stats.totali.totaleDovuto)}</StatValue>
              </StatCard>
              <StatCard>
                <StatLabel>Incassato</StatLabel>
                <StatValue $color="#2E7D4F">{formatEuro(stats.totali.totalePagato)}</StatValue>
              </StatCard>
              <StatCard>
                <StatLabel>Da Incassare</StatLabel>
                <StatValue $color={colors.accent}>{formatEuro(stats.totali.totaleRimanente)}</StatValue>
              </StatCard>
              <StatCard>
                <StatLabel>Menu</StatLabel>
                {stats.perCategoria.map((c) => (
                  <StatLine key={c._id}>
                    {menuLabel(menuConfig, c._id)}: <strong>{c.count}</strong> ({formatEuro(c.incasso)})
                  </StatLine>
                ))}
              </StatCard>
              <StatCard>
                <StatLabel>Opzioni menu</StatLabel>
                {stats.perOpzione.map((o) => (
                  <StatLine key={`${o._id.chiave}-${o._id.valore}`}>
                    {o._id.valore}: <strong>{o.count}</strong>
                  </StatLine>
                ))}
              </StatCard>
              <StatCard>
                <StatLabel>Stato Pagamento</StatLabel>
                {stats.perTipoPagamento.map((t) => (
                  <StatLine key={t._id}>
                    {paymentTipoLabel(t._id)}: <strong>{t.count}</strong>
                  </StatLine>
                ))}
              </StatCard>
              <StatCard>
                <StatLabel>🍺 Bicchieri commemorativi</StatLabel>
                <StatValue>{stats.bicchieriCommemorativi.count} <span style={{ fontSize: 13, fontWeight: 400 }}>({formatEuro(stats.bicchieriCommemorativi.incasso)})</span></StatValue>
              </StatCard>
            </StatsGrid>
          )}

          {/* Filtri */}
          <FiltersBar>
            <FilterControl
              placeholder="Cerca per nome, email, prenotazione, gruppo..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ maxWidth: 280 }}
            />
            <FilterSelect value={categoriaFiltro} onChange={(e) => setCategoriaFiltro(e.target.value)}>
              <option value="tutte">Tutte le categorie menu</option>
              {menuConfig?.categorie.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </FilterSelect>
            <FilterSelect value={pagamentoFiltro} onChange={(e) => setPagamentoFiltro(e.target.value)}>
              <option value="tutti">Tutti i pagamenti</option>
              <option value="da_pagare">Da pagare</option>
              <option value="acconto">Acconto</option>
              <option value="completo">Completo</option>
            </FilterSelect>
            <FilterSelect value={tavoloFiltro} onChange={(e) => setTavoloFiltro(e.target.value)}>
              <option value="tutti">Tutti i tavoli</option>
              <option value="assegnato">Tavolo assegnato</option>
              <option value="non_assegnato">Tavolo non assegnato</option>
            </FilterSelect>
            <div style={{ fontSize: 13, color: colors.lightBrown, alignSelf: 'center' }}>
              {filteredRegistrations.length} / {registrations.length} prenotazioni
            </div>
          </FiltersBar>

          {/* Totali sui risultati filtrati */}
          <StatsGrid>
            <StatCard $highlight>
              <StatLabel>Prenotazioni (filtrate)</StatLabel>
              <StatValue>{filteredTotals.totaleRegistrazioni}</StatValue>
            </StatCard>
            <StatCard $highlight>
              <StatLabel>Partecipanti (filtrati)</StatLabel>
              <StatValue>{filteredTotals.totalePartecipanti}</StatValue>
            </StatCard>
            <StatCard $highlight>
              <StatLabel>Totale Dovuto (filtrato)</StatLabel>
              <StatValue>{formatEuro(filteredTotals.totaleDovuto)}</StatValue>
            </StatCard>
            <StatCard $highlight>
              <StatLabel>Incassato (filtrato)</StatLabel>
              <StatValue $color="#2E7D4F">{formatEuro(filteredTotals.totalePagato)}</StatValue>
            </StatCard>
            <StatCard $highlight>
              <StatLabel>Da Incassare (filtrato)</StatLabel>
              <StatValue $color={colors.accent}>{formatEuro(filteredTotals.totaleRimanente)}</StatValue>
            </StatCard>
            <StatCard $highlight>
              <StatLabel>Menu (filtrati)</StatLabel>
              {filteredTotals.perCategoria.map((c) => (
                <StatLine key={c._id}>
                  {c._id}: <strong>{c.count}</strong> ({formatEuro(c.incasso)})
                </StatLine>
              ))}
            </StatCard>
            <StatCard $highlight>
              <StatLabel>🍺 Bicchieri (filtrati)</StatLabel>
              <StatValue>{filteredTotals.bicchieri.count} <span style={{ fontSize: 13, fontWeight: 400 }}>({formatEuro(filteredTotals.bicchieri.incasso)})</span></StatValue>
            </StatCard>
          </StatsGrid>

          {/* Tabella */}
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Prenotazione</Th>
                  <Th>Nome</Th>
                  <Th>Cognome</Th>
                  <Th>Telefono</Th>
                  <Th>Email</Th>
                  <Th>Gruppo</Th>
                  <Th>Tavolo</Th>
                  <Th>Partecipanti</Th>
                  <Th>Menu</Th>
                  <Th>Pagamento</Th>
                  <Th>Note</Th>
                  <Th>Azioni</Th>
                </tr>
              </thead>
              <tbody>
                {filteredRegistrations.map((reg) => (
                  <TableRow key={reg._id}>
                    <Td style={{ fontFamily: 'monospace' }}>{reg.bookingNumber}</Td>
                    <Td>
                      <CellInput value={getFieldValue(reg, 'nome')} onChange={(e) => setFieldValue(reg.bookingNumber, 'nome', e.target.value)} />
                    </Td>
                    <Td>
                      <CellInput value={getFieldValue(reg, 'cognome')} onChange={(e) => setFieldValue(reg.bookingNumber, 'cognome', e.target.value)} />
                    </Td>
                    <Td>
                      <CellInput value={getFieldValue(reg, 'telefono')} onChange={(e) => setFieldValue(reg.bookingNumber, 'telefono', e.target.value)} />
                    </Td>
                    <Td>
                      <CellInput value={getFieldValue(reg, 'email')} onChange={(e) => setFieldValue(reg.bookingNumber, 'email', e.target.value)} />
                    </Td>
                    <Td>
                      <CellInput value={getFieldValue(reg, 'nomeGruppo')} onChange={(e) => setFieldValue(reg.bookingNumber, 'nomeGruppo', e.target.value)} />
                    </Td>
                    <Td>
                      <CellInput style={{ minWidth: 50 }} value={getFieldValue(reg, 'numeroTavolo')} onChange={(e) => setFieldValue(reg.bookingNumber, 'numeroTavolo', e.target.value)} />
                    </Td>
                    <Td style={{ textAlign: 'center' }}>{reg.numeroPartecipanti}</Td>
                    <Td style={{ minWidth: 200 }}>{formatMenuRiepilogo(reg, menuConfig)}</Td>
                    <Td style={{ minWidth: 150 }}>
                      {editingPaymentBooking === reg.bookingNumber ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <label style={{ fontSize: 11, color: colors.lightBrown }}>
                            Dovuto
                            <CellInput
                              type="number" step="0.01" min="0"
                              value={paymentDraft.totaleDovuto}
                              onChange={(e) => setPaymentDraft((p) => ({ ...p, totaleDovuto: e.target.value }))}
                            />
                          </label>
                          <label style={{ fontSize: 11, color: colors.lightBrown }}>
                            Pagato
                            <CellInput
                              type="number" step="0.01" min="0"
                              value={paymentDraft.importoPagato}
                              onChange={(e) => setPaymentDraft((p) => ({ ...p, importoPagato: e.target.value }))}
                            />
                          </label>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <Btn $size="sm" onClick={() => savePayment(reg)} disabled={isSavingPayment}>
                              {isSavingPayment ? '...' : 'Salva'}
                            </Btn>
                            <Btn $size="sm" $variant="secondary" onClick={cancelEditPayment} disabled={isSavingPayment}>
                              Annulla
                            </Btn>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <PaymentBadge $tipo={reg.payment.tipo}>{paymentTipoLabel(reg.payment.tipo)}</PaymentBadge>
                          <div style={{ fontSize: 12, marginTop: 4 }}>Dovuto: {formatEuro(reg.totaleDovuto)}</div>
                          <div style={{ fontSize: 12 }}>Pagato: {formatEuro(reg.importoPagato)}</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: reg.saldoRimanente > 0 ? colors.accent : '#2E7D4F' }}>
                            Saldo: {formatEuro(reg.saldoRimanente)}
                          </div>
                          <LinkButton onClick={() => startEditPayment(reg)}>✏️ correggi</LinkButton>
                        </div>
                      )}
                    </Td>
                    <Td>
                      <CellInput value={getFieldValue(reg, 'note')} onChange={(e) => setFieldValue(reg.bookingNumber, 'note', e.target.value)} />
                    </Td>
                    <Td style={{ whiteSpace: 'nowrap' }}>
                      <Btn
                        $size="sm"
                        $variant={isDirty(reg.bookingNumber) ? 'primary' : 'secondary'}
                        onClick={() => handleSave(reg)}
                        disabled={!isDirty(reg.bookingNumber) || savingBooking === reg.bookingNumber}
                        style={{ marginRight: 6 }}
                      >
                        Salva
                      </Btn>
                      <Btn
                        $size="sm"
                        $variant="danger"
                        onClick={() => handleDelete(reg)}
                        disabled={savingBooking === reg.bookingNumber}
                      >
                        Elimina
                      </Btn>
                    </Td>
                  </TableRow>
                ))}
                {filteredRegistrations.length === 0 && (
                  <tr>
                    <Td colSpan={12}>Nessuna prenotazione trovata.</Td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableWrapper>
        </>
      )}
    </PageWrapper>
  );
};

const AdminPage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(adminApiService.isAuthenticated());

  const handleLogout = () => {
    adminApiService.logout();
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) {
    return <LoginForm onSuccess={() => setIsAuthenticated(true)} />;
  }

  return <Dashboard onLogout={handleLogout} />;
};

export default AdminPage;
